import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";
import apiService from "../rest";
import {
  APPLE_PAY_PAYMENT_TYPE,
  ERROR_PROCESSING_AP_PAYMENT,
  MAPI_AP_PROCESSOR,
  FREEDOMPAY_AP_PROCESSOR,
  SENTRY_ERRORS
} from "../../app-constants";

class CheckoutAPService {
  /**
   * Version of Apple Pay version you're using
   * IW uses oldest possible version
   */
  static VERSION_NUMBER = 1;
  static DEFAULT_CB = () => {};
  /**
   * AP checkout, only works with Safari.
   * @link https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession
   * @param payload
   * @param dispatch
   * @param commit
   * @param isFreedomPay
   * @returns {Promise<Promise<never>|undefined>}
   */
  constructor(payload, dispatch, commit, isFreedomPay) {
    this.commit = commit;
    this.dispatch = dispatch;
    this.payload = payload;
    this.venueId = payload.venueId;
    this.onErrorCB = payload.onError || CheckoutAPService.DEFAULT_CB;
    this.onSuccessCB = payload.onSuccess || CheckoutAPService.DEFAULT_CB;
    this.onCatchCB = payload.onCatch || CheckoutAPService.DEFAULT_CB;
    this.onCancelCB = payload.onCancel || CheckoutAPService.DEFAULT_CB;
    this.isFreedomPay = isFreedomPay;
  }

  init() {
    try {
      // init start sessions
      this.applePaySession = this.initConventionalApplePaySession();

      // configure handlers
      this.handleSessionEvents();

      // trigger listeners
      this.applePaySession.begin();
    } catch (error) {
      errorLoggerAdapter.createIssue(
        SENTRY_ERRORS.ERROR_MAPI_FP_AP_INIT,
        "initAPError",
        "CheckoutAPService.init",
        { description: error.toString() || "" },
        error
      );

      this.onErrorCB(ERROR_PROCESSING_AP_PAYMENT);
    }
  }

  /**
   * Initalizes a Conventional Apple Pay Session
   * @return {ApplePaySession}
   */
  initConventionalApplePaySession() {
    const {
      balance,
      vendorName,
      countryCode = "US",
      currencyCode = "USD",
      requiredBillingContactFields = ["postalAddress", "name"],
      supportedNetworks = ["amex", "discover", "masterCard", "visa"],
      merchantCapabilities = ["supports3DS", "supportsCredit", "supportsDebit"]
    } = this.payload;

    const config = {
      merchantCapabilities,
      supportedNetworks,
      countryCode,
      currencyCode,
      requiredBillingContactFields,
      total: {
        label: `${vendorName}:`,
        amount: balance
      }
    };

    return new ApplePaySession(CheckoutAPService.VERSION_NUMBER, config);
  }

  /**
   * Configure Session Event Handlers
   * - onvalidatemerchant
   * - onpaymentauthorized
   */
  handleSessionEvents() {
    this.applePaySession.onvalidatemerchant = this.validateMerchantHandler();
    this.applePaySession.onpaymentauthorized = this.paymentAuthorizationHandler();

    // this is was added to check error event handling in checkout component
    this.applePaySession.oncancel = (event) => {
      this.onCancelCB();
    };
  }

  /**
   * Validate Merchant Handler
   * @link https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778021-onvalidatemerchant
   */
  validateMerchantHandler() {
    return async (event) => {
      try {
        const getAuthResult = await apiService.applePayGetAuth();
        const token = getAuthResult?.data?.auth_key || "";

        const payload = {
          reqBody: {
            url: event.validationURL,
            venue_id: `${this.venueId}`,
            processor: this.isFreedomPay ? FREEDOMPAY_AP_PROCESSOR : MAPI_AP_PROCESSOR
          },
          token
        };

        const merchantSession = await apiService.applePayGetSession(payload);
        if (merchantSession && !merchantSession.success && !merchantSession.data) {
          throw new Error(merchantSession.errors);
        }
        this.applePaySession.completeMerchantValidation(merchantSession.data);
      } catch (error) {
        errorLoggerAdapter.createIssue(
          SENTRY_ERRORS.ERROR_MAPI_FP_AP_VALIDATE,
          "checkoutError",
          "CheckoutAPService.validateMerchantHandler.catch",
          { description: error.toString() },
          error
        );

        this.onCatchCB(error);
        this.onErrorCB(ERROR_PROCESSING_AP_PAYMENT);
        try {
          this.applePaySession.abort();
          this.applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
        } catch (e) {
          errorLoggerAdapter.createIssue(
            SENTRY_ERRORS.ERROR_MAPI_FP_AP_VALIDATE,
            "checkoutError",
            "CheckoutAPService.validateMerchantHandler.applePaySession.completePayment.catch",
            { description: e.toString() },
            e
          );
        }
      }
    };
  }

  /**
   * Payment Authorization Handler
   * @link https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778020-onpaymentauthorized
   */
  paymentAuthorizationHandler() {
    return async (event) => {
      if (!event?.payment?.token?.paymentData) {
        errorLoggerAdapter.createIssue(
          SENTRY_ERRORS.ERROR_MAPI_FP_PAYSHEET_PAYMENT_AUTH_FAILED,
          "checkoutError",
          "CheckoutAPService.paymentAuthorizationHandler.event",
          {
            description: JSON.stringify(event)
          }
        );
        this.onErrorCB(ERROR_PROCESSING_AP_PAYMENT);
        try {
          this.applePaySession.abort();
          this.applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
        } catch (error) {
          errorLoggerAdapter.createIssue(
            SENTRY_ERRORS.ERROR_MAPI_FP_PAYSHEET_PAYMENT_AUTH_FAILED,
            "checkoutError",
            "CheckoutAPService.applePaySession.completePayment.event",
            { description: error.toString() },
            error
          );
        }

        return;
      }

      this.processAPToken(event.payment.token.paymentData, event.payment.billingContact);
    };
  }

  /**
   * Upon successful ApplePayment, send payment token through connect checkout.
   * @param paymentData
   * @param billingContact
   * @returns {Promise<void>}
   */
  async processAPToken(paymentData, billingContact) {
    const { fee, tip, tax, subtotalAmount, amount, payments, balance } = this.payload;

    // AP payment token.
    const paymentInfo = btoa(JSON.stringify(paymentData));
    const nameOnCard = billingContact ? `${billingContact.givenName} ${billingContact.familyName}` : null;
    const paymentsData = payments.concat([
      {
        balance,
        opaqueDataValue: paymentInfo,
        paymentType: APPLE_PAY_PAYMENT_TYPE
      }
    ]);

    const checkoutPayload = {
      payments: paymentsData,
      nameOnCard,
      fee,
      tip,
      tax,
      subtotalAmount,
      amount
    };

    try {
      const { order } = await this.dispatch("cart/checkoutCreditCard", checkoutPayload, { root: true });
      this.onSuccessCB(order);
      this.applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
    } catch (error) {
      errorLoggerAdapter.createIssue(
        SENTRY_ERRORS.ERROR_MAPI_AP_PROCESS,
        "checkoutError",
        "CheckoutAPService.processAPToken.catch",
        { description: error.toString() },
        error
      );

      this.onErrorCB(ERROR_PROCESSING_AP_PAYMENT);
      try {
        this.applePaySession.abort();
        this.applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
      } catch (e) {
        errorLoggerAdapter.createIssue(
          SENTRY_ERRORS.ERROR_MAPI_AP_PROCESS,
          "checkoutError",
          "CheckoutAPService.processAPToken.applePaySession.completePayment",
          { description: e.toString() },
          e
        );
      }
    }
  }
}

export default CheckoutAPService;
